import { TimelineSnapshot } from '../lib/TimelineSnapshot';
import { TimelineMode } from './TimelineMode';

import type { Element } from '../models/Element';
import type { ExtraElementData } from '../models/ExtraElementData';
import type { VideoEditor } from '../VideoEditor';
import type { Command } from './Command';

/**
 * Common options for storing affected elements
 */
export interface AffectedElementOptions {
  moveAmount?: number;
  newEndTime?: number;
  newStartTime?: number;
  filter?: (element: Element, sourceElement: Element) => boolean;
}

/**
 * Common options for applying ripple effects
 */
export interface RippleOptions {
  rippleAmount: number;
  newEndTime?: number;
}

/**
 * Abstract base class for command implementation.
 * Uses direct object references instead of IDs.
 */
export abstract class AbstractCommand implements Command {
  protected editor: VideoEditor;
  protected sourceElement: Element | undefined;
  // Store full element state as dynamic records with direct references
  protected affectedElements: Array<{
    element: Element;
    originalState: Record<string, any>;
  }>;
  // Timeline snapshot for complete undo
  protected timelineSnapshot: TimelineSnapshot | null = null;

  getName(): string {
    return 'AbstractCommand';
  }

  shouldCreateUndoRedoEntry(): boolean {
    return true;
  }

  /**
   * Constructor for AbstractCommand
   * @param editor VideoEditor instance
   * @param sourceElement Source element for the command (optional)
   */
  constructor(editor: VideoEditor, sourceElement?: Element) {
    this.editor = editor;
    this.affectedElements = [];
    this.sourceElement = sourceElement;
  }

  /**
   * Returns the current timeline mode.
   */
  protected getTimelineMode(): TimelineMode {
    return this.editor.getTimelineMode();
  }

  /**
   * Recalculates the timeline duration.
   */
  protected recalculateTimeline(): void {
    this.editor.timeline.recalculateDuration();
  }

  /**
   * Captures the full mutable state of an element.
   * @param el The element to capture state from.
   * @returns A record of the element's mutable properties.
   */
  protected captureElementState(el: Element): Record<string, any> {
    return el.getState();
  }

  /**
   * Restores an element's full state from a captured snapshot.
   * @param element The element to restore.
   * @param state The state to restore.
   */
  protected restoreElementState(
    element: Element,
    state: Record<string, any>,
  ): void {
    element.restoreState(state);
  }

  /**
   * Creates a snapshot of the current timeline state.
   * Call this at the beginning of execute() before making any changes to use default undo behavior.
   */
  protected captureTimelineSnapshot(): void {
    this.timelineSnapshot = new TimelineSnapshot(this.editor.timeline);
  }

  /**
   * Restores the timeline state from the saved snapshot.
   * Call this in undo() to revert changes made by execute().
   * @returns True if the restore was successful
   */
  protected restoreTimelineSnapshot(): boolean {
    if (!this.timelineSnapshot) {
      console.error('No timeline snapshot to restore');
      return false;
    }
    return this.timelineSnapshot.apply(this.editor.timeline);
  }

  /**
   * Stores affected elements (for ripple operations) by capturing full state.
   */
  protected storeAffectedElements(options: AffectedElementOptions): void {
    if (!this.sourceElement) return;

    // Get the source track
    const sourceTrack = this.editor.timeline.getTrackForElement(
      this.sourceElement.id,
    );

    if (!sourceTrack) return;

    // Calculate source end time using getters for encapsulated properties
    const sourceEndTime =
      this.sourceElement.time + this.sourceElement.durationSeconds;

    // The ! is needed to tell TypeScript that sourceElement is defined
    // We've already checked for undefined at the beginning of the method
    const defaultFilter = (element: Element) =>
      element.id !== this.sourceElement!.id && element.time >= sourceEndTime;
    const filterFn = options.filter || defaultFilter;

    // Get elements from the source track
    this.affectedElements = [...sourceTrack.elements]
      // The ! is needed here too to tell TypeScript that sourceElement is defined
      .filter(element => filterFn(element, this.sourceElement!))
      .sort((a, b) => a.time - b.time)
      .map(element => ({
        element,
        originalState: this.captureElementState(element),
      }));
  }

  /**
   * Restores all affected elements to their original state.
   */
  protected restoreAffectedElements(): void {
    if (
      this.getTimelineMode() === TimelineMode.RIPPLE &&
      this.affectedElements.length > 0
    ) {
      this.affectedElements.forEach(({ element, originalState }) => {
        this.restoreElementState(element, originalState);
      });
    }
  }

  /**
   * Snaps a time value to the nearest frame boundary if enabled.
   */
  protected snapTimeToFrame(time: number): number {
    return this.editor.snapTimeToFrame(time);
  }

  /**
   * Execute the command
   * Default implementation captures a timeline snapshot before executing
   * Subclasses should call super.execute() at the beginning of their execute method
   */
  execute(): boolean {
    // Capture timeline state before any modifications
    this.captureTimelineSnapshot();
    return true;
  }

  /**
   * Undo the command
   * Default implementation restores the timeline from the snapshot
   * Subclasses can override this for more specific behavior
   */
  undo(): boolean {
    return this.restoreTimelineSnapshot();
  }

  abstract getDescription(): string;

  get extraElementData(): ExtraElementData {
    return this.editor.getTrackManager().extraElementData;
  }
}

/**
 * Abstract base class for element-based command implementations.
 * Ensures that sourceElement is required and properly typed.
 */
export abstract class AbstractElementCommand extends AbstractCommand {
  protected sourceElement: Element;

  /**
   * Constructor for AbstractElementCommand
   * @param editor VideoEditor instance
   * @param sourceElement Source element for the command (required)
   */
  constructor(editor: VideoEditor, sourceElement: Element) {
    super(editor, sourceElement);
    this.sourceElement = sourceElement; // Ensure proper typing
  }

  /**
   * Stores affected elements (for ripple operations) by capturing full state.
   * Override to ensure proper typing with non-optional sourceElement.
   */
  protected storeAffectedElements(options: AffectedElementOptions): void {
    // Get the source track
    const sourceTrack = this.editor.timeline.getTrackForElement(
      this.sourceElement.id,
    );

    if (!sourceTrack) return;

    // Calculate source end time using getters for encapsulated properties
    const sourceEndTime =
      this.sourceElement.time + this.sourceElement.durationSeconds;

    const defaultFilter = (element: Element) =>
      element.id !== this.sourceElement.id && element.time >= sourceEndTime;
    const filterFn = options.filter || defaultFilter;

    // Get elements from the source track
    this.affectedElements = [...sourceTrack.elements]
      .filter(element => filterFn(element, this.sourceElement))
      .sort((a, b) => a.time - b.time)
      .map(element => ({
        element,
        originalState: this.captureElementState(element),
      }));
  }
}
